import React, { Component, ReactNode } from 'react'
import { View, StyleSheet } from 'react-native'
import * as Sentry from '@sentry/react-native'
// Import LanguageContext directly (not via the @context barrel): the barrel
// pulls AuthProvider → @store → expo-secure-store, which breaks under Jest and
// bloats this module's graph. Same reason the calendar-sync hook avoids barrels.
import { useLanguage } from '../../../context/LanguageContext'
import { Text } from '../../atoms'
import { Button } from '../../molecules/Button'

interface FallbackLabels {
  title: string
  message: string
  retry: string
}

interface ErrorBoundaryProps {
  children: ReactNode
  labels: FallbackLabels
}

interface ErrorBoundaryState {
  hasError: boolean
}

// A class component is required here: only class components can act as React
// error boundaries (getDerivedStateFromError / componentDidCatch). It catches
// any render or lifecycle error thrown below it, reports it to Sentry, and
// shows a recoverable fallback instead of letting the whole app crash — which
// is what the App Store review flagged when an unhandled render error escalated
// to RCTFatal.
class ErrorBoundaryInner extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    })
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false })
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    const { labels } = this.props
    return (
      <View style={styles.container}>
        <Text type="bodySmallBold" style={styles.title}>
          {labels.title}
        </Text>
        <Text type="bodySmall" style={styles.message}>
          {labels.message}
        </Text>
        <Button title={labels.retry} onPress={this.handleRetry} />
      </View>
    )
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  title: {
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    textAlign: 'center',
    marginBottom: 24,
  },
})

// Functional wrapper so the fallback copy is translated: reading `useLanguage()`
// here (a hook) and passing the strings down keeps the class boundary hook-free,
// as React requires. Mount it high in the tree (below the providers it needs:
// LanguageProvider for `t`, NavigationContainer for the theme the atoms read).
export const RootErrorBoundary = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const { t } = useLanguage()
  return (
    <ErrorBoundaryInner
      labels={{
        title: t('errorBoundary.title'),
        message: t('errorBoundary.message'),
        retry: t('common.retry'),
      }}
    >
      {children}
    </ErrorBoundaryInner>
  )
}

// Exported for tests: lets a spec render the raw boundary with static labels.
export { ErrorBoundaryInner }
